import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PhoneVerify from "@/components/site/PhoneVerify";
import { api } from "@/lib/api";
import { botMessage, useBotCheck } from "@/components/site/BotCheck";
import { formatDate, humanize } from "@/lib/format";

const Note = ({ children, tone = "neutral", testId }) => (
  <p role="status" className={`rounded-2xl px-4 py-3 text-sm leading-6 ${tone === "success" ? "cw-tone-success" : tone === "error" ? "cw-tone-error" : "bg-[var(--cw-surface-tertiary)]"}`} data-testid={testId}>{children}</p>
);

function AppealForm({ token, info, onSent }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const max = info.appeal_max_chars;
  const bot = useBotCheck("appeal");
  const send = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try { const t = await bot.getToken(); const r = await api("/public/appeal", { token, body: { text: text.trim(), ...bot.fields(t) } }); onSent(r); }
    catch (err) { setError(botMessage(err) || (err.code === "already_appealed" ? "You've already appealed this decision. We'll email you when it's reviewed." : err.message)); }
    finally { bot.reset(); setBusy(false); }
  };
  return (
    <form onSubmit={send} className="relative space-y-4" data-testid="appeal-form">
      {bot.honeypot}
      <p className="cw-soft text-sm">Your account was restricted{info.category ? ` (${humanize(info.category)})` : ""}. Tell us what you think we got wrong. A different person from the one who decided will review it.</p>
      <div><Label htmlFor="appeal-text">Your appeal</Label>
        <Textarea id="appeal-text" rows={6} maxLength={max} value={text} onChange={(e) => setText(e.target.value)} aria-describedby="appeal-count" className="mt-2" data-testid="appeal-text-input" />
        <p id="appeal-count" className="cw-muted mt-1 text-xs tabular-nums">{text.length} / {max}</p></div>
      {bot.slot}
      {error && <p role="alert" className="cw-error text-sm" data-testid="appeal-error">{error}</p>}
      <Button type="submit" disabled={busy || text.trim().length < 3} className="h-12 rounded-full px-6" data-testid="appeal-submit-button">{busy ? "Sending…" : "Send appeal"}</Button>
    </form>
  );
}

export default function AppealFlow({ intro, phoneLabel, buttonLabel }) {
  const [token, setToken] = useState(null);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const onVerified = async (t) => {
    setToken(t);
    try { setInfo(await api("/public/appeal", { token: t })); } catch (e) { setError(e.message); }
  };
  if (!token) return <PhoneVerify purpose="appeal" onVerified={onVerified} intro={intro} phoneLabel={phoneLabel} buttonLabel={buttonLabel} />;
  if (error) return <Note tone="error" testId="appeal-load-error">{error}</Note>;
  if (!info) return <p className="cw-muted text-sm">Checking…</p>;
  if (!info.has_ban) return <Note testId="appeal-no-ban">{info.message}</Note>;
  if (info.appeal) return <Note testId="appeal-status">Your appeal from {formatDate(info.appeal.created_at, false)} is <strong>{humanize(info.appeal.status)}</strong>{". We'll email you when it's reviewed."}</Note>;
  if (!info.can_appeal) return <Note testId="appeal-cannot">{"This decision can't be appealed again."}</Note>;
  return <AppealForm token={token} info={info} onSent={(r) => setInfo((i) => ({ ...i, appeal: r.appeal }))} />;
}
